import { useId } from "react"

import { clsx } from "clsx"

import {
  LIGHTREACH_CONTRACT_ACTION_ERROR,
  LIGHTREACH_CONTRACT_HEADING,
  LIGHTREACH_CONTRACT_NEEDS_ATTENTION_LABEL,
  LIGHTREACH_NO_CONTRACT_MESSAGE,
  LIGHTREACH_REFRESH_CONTRACT_LABEL,
  LIGHTREACH_REFRESHING_CONTRACT_LABEL,
  LIGHTREACH_RETRY_SUPPLEMENTAL_LABEL,
  LIGHTREACH_RETRYING_SUPPLEMENTAL_LABEL,
  LIGHTREACH_SEND_CONTRACT_LABEL,
  LIGHTREACH_SENDING_CONTRACT_LABEL,
  LIGHTREACH_VOID_CONTRACT_LABEL,
  LIGHTREACH_VOIDING_CONTRACT_LABEL,
  lightReachContractClosed,
  lightReachContractState,
  lightReachSupplementalState,
} from "./lightreach-application-copy"
import type {
  LightReachApplicationBundle,
  LightReachContractCommand,
  LightReachFinancingView,
} from "./lightreach-kind-bundle"
import { OUTCOME_TONE_CLASS } from "../concert/concert-application-copy"

const sectionClass = `rounded-lg border border-bp-border bg-bp-elevated`
const badgeClass = `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold`
const secondaryButtonClass = `rounded-md border border-bp-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-semibold text-bp-text hover:bg-bp-elevated-2 disabled:cursor-not-allowed disabled:opacity-60`

/** A kind without Void contract has no command for it. */
const useNoVoidCommand = (): null => null

/**
 * The LightReach contract for an approved application. LightReach sends the
 * contract while stipulations are open, so the server's send action alone
 * decides whether Send contract is offered. A kind with a void command offers
 * Void contract while the read allows it (an unsigned contract only).
 */
export function LightReachContract({
  bundle,
  proposalId,
  financing,
}: {
  bundle: LightReachApplicationBundle
  proposalId: string
  financing: LightReachFinancingView
}): JSX.Element | null {
  const { testIdPrefix } = bundle
  const mutation = bundle.useContract()
  const useVoidCommand = bundle.useVoidContract ?? useNoVoidCommand
  const voidCommand = useVoidCommand()
  const headingId = useId()
  const sendReasonId = useId()
  const contract = financing.lightReachContract ?? null
  const { sendLightReachContract, refreshLightReachContract } = financing.actions
  const retrySupplemental = financing.actions.retryLightReachSupplemental
  const canVoid =
    contract != null &&
    financing.actions.voidLightReachContract?.allowed === true
  const busy = mutation.isLoading || voidCommand?.isLoading === true

  // Nothing to show before the application can carry a contract.
  if (
    contract == null &&
    !sendLightReachContract.allowed &&
    !refreshLightReachContract.allowed
  ) {
    return null
  }

  const pending = mutation.isLoading ? mutation.variables?.command.kind : null
  const run = (command: LightReachContractCommand): void => {
    mutation.mutate({ proposalId, command })
  }
  // Where the kind allows it, a voided or rejected contract makes way for a
  // new one once the server allows sending again.
  const canSendAgain =
    bundle.resendAfterClose === true && sendLightReachContract.allowed
  // A drafted contract LightReach never sent can still be sent, and a new one
  // after a closed contract only where the kind allows it.
  const sendable =
    contract == null ||
    (contract.status === `created` && sendLightReachContract.allowed) ||
    (lightReachContractClosed(contract) && canSendAgain)
  const state =
    contract != null
      ? lightReachContractState(
          contract,
          { send: sendLightReachContract, refresh: refreshLightReachContract },
          {
            canSendAgain,
          },
        )
      : null
  const supplemental = contract?.supplemental ?? null
  const supplementalState =
    supplemental != null ? lightReachSupplementalState(supplemental) : null

  return (
    <section
      aria-labelledby={headingId}
      data-testid={`${testIdPrefix}-lightreach-contract`}
      className={sectionClass}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
        <h3
          id={headingId}
          className="text-[length:var(--step--1)] font-semibold text-bp-text"
        >
          {LIGHTREACH_CONTRACT_HEADING}
        </h3>
        {state != null ? (
          <span
            role="status"
            data-testid={`${testIdPrefix}-lightreach-contract-badge`}
            className={clsx(badgeClass, OUTCOME_TONE_CLASS[state.tone])}
          >
            {state.badge}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-3 px-4 py-4 text-[length:var(--step--2)]">
        {state != null && contract != null ? (
          <div>
            <p
              data-testid={`${testIdPrefix}-lightreach-contract-detail`}
              className="text-[length:var(--step--1)] text-bp-text"
            >
              {state.detail}
            </p>
            <p className="mt-1 text-bp-text-muted tabular-nums">
              LightReach contract {contract.providerReference}
            </p>
          </div>
        ) : (
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            {LIGHTREACH_NO_CONTRACT_MESSAGE}
          </p>
        )}

        {contract?.needsAttentionReason != null ? (
          <div
            role="alert"
            data-testid={`${testIdPrefix}-lightreach-contract-needs-attention`}
            className="flex flex-col gap-1 rounded-md border border-bp-gold/40 bg-bp-gold/10 px-3 py-2 text-bp-text"
          >
            <span className="font-semibold text-bp-gold">
              {LIGHTREACH_CONTRACT_NEEDS_ATTENTION_LABEL}
            </span>
            <span>{contract.needsAttentionReason}</span>
          </div>
        ) : null}

        {supplemental != null && supplementalState != null ? (
          <div
            data-testid={`${testIdPrefix}-lightreach-supplemental`}
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <span className="text-bp-text">Powur supplemental documents</span>
            <span
              className={clsx(
                badgeClass,
                OUTCOME_TONE_CLASS[supplementalState.tone],
              )}
            >
              {supplementalState.label}
            </span>
            {supplemental.failureMessage != null ? (
              <p className="w-full text-bp-text-muted">
                {supplemental.failureMessage}
              </p>
            ) : null}
          </div>
        ) : null}

        {contract == null && sendLightReachContract.reason != null ? (
          <p
            id={sendReasonId}
            data-testid={`${testIdPrefix}-lightreach-send-contract-reason`}
            className="text-bp-text-muted"
          >
            {sendLightReachContract.reason}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          {sendable ? (
            <button
              type="button"
              data-testid={`${testIdPrefix}-lightreach-send-contract`}
              aria-describedby={
                sendLightReachContract.reason != null ? sendReasonId : undefined
              }
              disabled={!sendLightReachContract.allowed || busy}
              onClick={() => run({ kind: `send` })}
              className="rounded-md bg-bp-accent px-3 py-1.5 text-[length:var(--step--2)] font-semibold text-bp-on-accent hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {pending === `send`
                ? LIGHTREACH_SENDING_CONTRACT_LABEL
                : LIGHTREACH_SEND_CONTRACT_LABEL}
            </button>
          ) : null}
          {refreshLightReachContract.allowed ? (
            <button
              type="button"
              data-testid={`${testIdPrefix}-lightreach-refresh-contract`}
              disabled={busy}
              onClick={() => run({ kind: `refresh` })}
              className={secondaryButtonClass}
            >
              {pending === `refresh`
                ? LIGHTREACH_REFRESHING_CONTRACT_LABEL
                : LIGHTREACH_REFRESH_CONTRACT_LABEL}
            </button>
          ) : null}
          {supplemental != null && retrySupplemental.allowed ? (
            <button
              type="button"
              data-testid={`${testIdPrefix}-lightreach-retry-supplemental`}
              disabled={busy}
              onClick={() =>
                run({
                  kind: `retrySupplemental`,
                  attemptId: supplemental.attemptId,
                })
              }
              className={secondaryButtonClass}
            >
              {pending === `retrySupplemental`
                ? LIGHTREACH_RETRYING_SUPPLEMENTAL_LABEL
                : LIGHTREACH_RETRY_SUPPLEMENTAL_LABEL}
            </button>
          ) : null}
          {canVoid && voidCommand != null ? (
            <button
              type="button"
              data-testid={`${testIdPrefix}-lightreach-void-contract`}
              disabled={busy}
              onClick={() => voidCommand.mutate({ proposalId })}
              className={secondaryButtonClass}
            >
              {voidCommand.isLoading
                ? LIGHTREACH_VOIDING_CONTRACT_LABEL
                : LIGHTREACH_VOID_CONTRACT_LABEL}
            </button>
          ) : null}
        </div>

        {mutation.isError && !mutation.isLoading ? (
          <p role="alert" className="text-bp-gold">
            {bundle.errorMessage(mutation.error) ??
              LIGHTREACH_CONTRACT_ACTION_ERROR}
          </p>
        ) : null}
        {voidCommand?.isError === true && !voidCommand.isLoading ? (
          <p role="alert" className="text-bp-gold">
            {bundle.errorMessage(voidCommand.error) ??
              LIGHTREACH_CONTRACT_ACTION_ERROR}
          </p>
        ) : null}
      </div>
    </section>
  )
}
